import { Component } from 'react';

import { container } from '../../../context/module';
import { Logger } from '../../../models/logger';
import { ApiConfig } from '../../../services/api-config';
import { FeatureRedirectService } from '../../../services/feature-redirect.service';
import { SuggestedPage } from '../../../services/file-validation.service';
import { LightHttp } from '../../../services/light-http.service';
import { MetricsService } from '../../../services/metrics.service';
import { SessionFileRecord, sessionCountHint, transferTray } from '../../../services/transfer-tray.service';
import { MetricEventName } from '../../../types/metric-events';
import { FileUtils } from '../../../utils/file-utils';
import styles from './session-files.component.module.scss';

interface SessionFilesProps {
    /** Extensions this page can take. Empty means it takes anything. */
    acceptedExtensions?: string[];
    pageSlug?: string;
    toolName?: string;
    pageData?: any;
    /**
     * `name:size` for every file already staged on this page. Those rows stay visible and say
     * "Added" -- hiding the list once one file is chosen would strand anyone adding a second.
     */
    stagedKeys?: string[];
    /** Stage these files here, through the same path a drop takes. */
    onPick: (files: File[]) => void;
    /**
     * "Re-run a conversion from this page", rendered as a second section of this panel.
     *
     * It used to be its own block further down the page, which meant a returning visitor saw two
     * near-identical panels -- "Files you used recently" and "You already converted a file here"
     * -- doing different things under labels that did not say so. One entry point, two labelled
     * sections.
     */
    reRunSection?: any;
    /** How many rows `reRunSection` will render, so the trigger can appear for it alone. */
    reRunCount?: number;
}

interface SessionFilesState {
    files: SessionFileRecord[];
    /** True until the store answers. While true, skeleton rows hold the space open. */
    loading: boolean;
    /** Rows to reserve before the store answers, read synchronously from localStorage. */
    reserve: number;
    /** The user opened the list. A shift caused by their own tap does not count against CLS. */
    expanded2: boolean;
    /** Id of the row whose destination choices are being fetched. */
    choosing: string | null;
    /** Destination options per row, once looked up. */
    choices: Record<string, SuggestedPage[]>;
    /** T6.4: show files this page cannot take, so a wrong pick can still be recovered. */
    showAll: boolean;
    /** Id of a file whose routing failed. A button that does nothing is worse than a refusal. */
    routeFailed: string | null;
}

/**
 * "Files you used recently" — pick one instead of finding it on disk again.
 *
 * The owner's D6 idea. It matters because the second job on a file is common and currently
 * costs a full round trip to the file manager, and because it shares one mechanism with the
 * wrong-file recovery path: both end in "here is a file, take it to the right tool".
 *
 * What it stores is the `File` itself, not a server handle. That keeps the whole feature
 * client-side — picking one runs the same selection path as a fresh drop — and it means a
 * remembered file can never be a dead link, so there is nothing to existence-check. It also
 * sidesteps the constraint that output files are not cross-edge readable yet
 * (docs/proposed/RETENTION_FIXES.md D6 constraint 3); that constraint is what still blocks
 * T6.3, which is about *outputs*, not the inputs offered here.
 *
 * Rendered below the tool. A list that arrives after hydration would shift anything beneath
 * it, and above the tool that would push the thing the visitor came for down the page — the
 * leak this whole workstream exists to close. Below the tool it starts off-screen, so the
 * shift costs nothing (D6 constraint 7).
 */
export class SessionFilesComponent extends Component<SessionFilesProps, SessionFilesState> {
    private readonly logger = Logger.getLogger('SessionFilesComponent');

    /**
     * Constructed rather than injected. `FeatureRedirectService` is not registered in the IoC
     * container -- `FileUploadManager` builds its own the same way -- so `@lazyInject` resolves
     * to nothing and every lookup fails silently. Found on QA: the route button did nothing,
     * with no error, because the failure landed in a catch that only logs.
     */
    private featureRedirectService = new FeatureRedirectService(
        container.get(LightHttp), container.get(ApiConfig));


    /**
     * `reserve` is read synchronously so the block occupies its final height on the first paint.
     * Without it the list appeared after hydration and pushed the ad and the options below it
     * down -- measured CLS 0.1022 on QA, over Google's 0.1 threshold. The hint can be stale
     * (cleared storage, an expired sweep); being wrong costs a small correction, never a wrong
     * list, because the rows themselves always come from the store.
     */
    readonly state: Readonly<SessionFilesState> = {
        files: [], showAll: false, routeFailed: null,
        loading: true,
        reserve: 0,
        expanded2: false,
        choosing: null,
        choices: {},
    };

    componentDidMount() {
        transferTray.session()
            .then(files => this.setState({ files, loading: false }))
            .catch(() => this.setState({ loading: false }));
    }

    /** Already staged on this page, so offering it again would just duplicate the file. */
    private isAdded(rec: SessionFileRecord): boolean {
        return (this.props.stagedKeys || []).includes(`${rec.name}:${rec.size}`);
    }

    private accepts(rec: SessionFileRecord): boolean {
        const accepted = this.props.acceptedExtensions;
        if (!accepted || accepted.length === 0) return true;
        return accepted.map(e => e.toLowerCase()).includes(rec.extension);
    }

    private async forget(rec: SessionFileRecord) {
        await transferTray.forget(rec.id).catch(() => { /* best-effort */ });
        this.setState({ files: this.state.files.filter(f => f.id !== rec.id) });
    }

    private pick(rec: SessionFileRecord) {
        try {
            container.get(MetricsService).emit(MetricEventName.SESSION_FILE_PICKED, {
                pageSlug: this.props.pageSlug || '',
                fileExtension: rec.extension,
                fromPage: rec.fromSlug || '',
            });
        } catch { /* telemetry is never load-bearing */ }
        this.setState({ expanded2: false });
        this.props.onPick([rec.file]);
    }

    /**
     * The file does not belong here. Show them where it could go -- do not pick for them.
     *
     * This used to be one button, "Open .jpg in the right tool", that navigated to whatever the
     * resolver ranked first. That claims a certainty we do not have: a JPG could want compressing,
     * converting, resizing or cropping, and only the person holding it knows which. Ranking is
     * useful for ordering the options; it is not a licence to choose on someone's behalf.
     */
    private async offerChoices(rec: SessionFileRecord) {
        this.setState({ choosing: rec.id, routeFailed: null });
        try {
            const pages = await this.featureRedirectService.findRelevantPages(rec.extension, this.props.pageData);
            if (!pages || pages.length === 0) {
                this.setState({ choosing: null, routeFailed: rec.id });
                return;
            }
            this.setState({ choices: { ...this.state.choices, [rec.id]: pages.slice(0, 4) } });
        } catch (e) {
            this.logger.error({ message: 'Could not look up destinations for a session file', error: e });
            this.setState({ choosing: null, routeFailed: rec.id });
        }
    }

    /** Take the file to the destination the user chose. */
    private async goTo(rec: SessionFileRecord, page: SuggestedPage) {
        try {
            container.get(MetricsService).emit(MetricEventName.SESSION_FILE_ROUTED, {
                pageSlug: this.props.pageSlug || '', fileExtension: rec.extension, toPage: page.url,
            });
        } catch { /* telemetry is never load-bearing */ }
        const xferId = await transferTray.put(
            [rec.file], this.props.pageSlug || '', this.props.toolName || '').catch(() => null);
        const href = xferId
            ? `${page.url}${page.url.includes('?') ? '&' : '?'}xfer=${encodeURIComponent(xferId)}`
            : page.url;
        window.location.href = href;
    }

    private renderRow(rec: SessionFileRecord, usable: boolean) {
        const added = this.isAdded(rec);
        const looking = this.state.choosing === rec.id;
        const choices = this.state.choices[rec.id];
        return (
            <li key={rec.id} className={`${styles.row} ${added ? styles.rowAdded : ''}`}>
                <span className={styles.meta}>
                    <span className={styles.name}>{rec.name}</span>
                    <span className={styles.size}>{FileUtils.toReadableBytes(rec.size, 'quantity')}</span>
                </span>
                <span className={styles.actions}>
                    {added ? (
                        // Stays listed rather than disappearing: seeing it marked is how the user
                        // knows it went in, and the row below it stays where they expect.
                        <span className={styles.added} aria-label={`${rec.name} is already added`}>✓ Added</span>
                    ) : usable ? (
                        <button type="button" className={styles.use} onClick={() => this.pick(rec)}>
                            Use this file
                        </button>
                    ) : (
                        <button type="button" className={styles.route} disabled={looking}
                            aria-expanded={!!choices}
                            onClick={() => this.offerChoices(rec)}>
                            {looking ? 'Finding tools…' : `Where can I use .${rec.extension}?`}
                        </button>
                    )}
                    <button type="button" className={styles.forget}
                        aria-label={`Forget ${rec.name}`} onClick={() => this.forget(rec)}>
                        ✕
                    </button>
                </span>

                {choices && (
                    // The options, not a decision. A .jpg could want compressing, converting,
                    // resizing or cropping, and only the person holding it knows which -- so the
                    // ranking orders the list and the user picks from it.
                    <div className={styles.choices}>
                        <span className={styles.choicesLabel}>Take {rec.name} to:</span>
                        {choices.map(page => (
                            <button key={page.url} type="button" className={styles.choice}
                                onClick={() => this.goTo(rec, page)}>
                                {page.title}
                            </button>
                        ))}
                    </div>
                )}

                {this.state.routeFailed === rec.id && (
                    // A control that silently does nothing is worse than one that refuses: the
                    // user clicks again, then concludes the page is broken.
                    <span className={styles.routeError} role="status">
                        We couldn&apos;t find a tool for .{rec.extension} right now.{' '}
                        <a href="/all-tools">Browse all tools</a>
                    </span>
                )}
            </li>
        );
    }

    render() {
        const { files, showAll, loading, reserve } = this.state;

        // This block sits above the fold on a phone, and the file list only exists after
        // IndexedDB answers -- which is after hydration. On a statically exported page the
        // server HTML cannot know whether there are files, so anything that appears here later
        // shifts the ad and the options below it: measured CLS 0.1042 on QA, over the 0.1
        // threshold, from a single 232px insert.
        //
        // So the height never changes. A short slot is present in the static HTML, and the
        // list opens into an overlay on tap. A shift the user caused by tapping does not count
        // against CLS, and someone with no recent files pays 28px of whitespace rather than a
        // 232px hole.
        const usableCount = files.filter(f => this.accepts(f)).length;
        const reRun = this.props.reRunCount || 0;
        if (!this.state.expanded2) {
            return (
                <div className={styles.slot}>
                    {!loading && (files.length > 0 || reRun > 0) && (
                        // A bordered control rather than a text link: this competes for
                        // attention with a large dashed drop zone directly above it, and an
                        // underlined phrase loses that fight -- reported as hard to see.
                        <button type="button" className={styles.trigger}
                            aria-expanded={false}
                            onClick={() => this.setState({ expanded2: true })}>
                            <span aria-hidden="true" className={styles.triggerIcon}>🕘</span>
                            {usableCount > 0
                                ? `Use a recent file (${usableCount})`
                                : files.length > 0
                                    ? `Recent files (${files.length})`
                                    : `Re-run a conversion (${reRun})`}
                        </button>
                    )}
                </div>
            );
        }

        if (files.length === 0 && reRun === 0) return null;

        const usable = files.filter(f => this.accepts(f));
        const rest = files.filter(f => !this.accepts(f));
        // Nothing this page can take and the user has not asked to see the rest: stay quiet
        // rather than opening with what this tool cannot do.
        if (usable.length === 0 && !showAll) {
            return (
                <section className={styles.session} aria-labelledby="session-files-heading">
                    <button type="button" className={styles.close}
                        aria-label="Close recent files" onClick={() => this.setState({ expanded2: false })}>✕</button>
                    <h2 id="session-files-heading" className={styles.heading}>Files you used recently</h2>
                    {files.length > 0 && <p className={styles.sub}>
                        None of them work on this page.{' '}
                        <button type="button" className={styles.link}
                            onClick={() => this.setState({ showAll: true })}>
                            Show them anyway
                        </button>{' '}
                        and we&apos;ll take you to a tool that fits.
                    </p>}
                    {this.props.reRunSection}
                </section>
            );
        }

        return (
            <section className={styles.session} aria-labelledby="session-files-heading">
                <button type="button" className={styles.close}
                    aria-label="Close recent files" onClick={() => this.setState({ expanded2: false })}>✕</button>
                <h2 id="session-files-heading" className={styles.heading}>Files you used recently</h2>
                <p className={styles.sub}>Pick one instead of finding it on your device again.</p>
                <ul className={styles.list}>
                    {usable.map(f => this.renderRow(f, true))}
                    {showAll && rest.map(f => this.renderRow(f, false))}
                </ul>
                {rest.length > 0 && (
                    <button type="button" className={styles.link}
                        aria-expanded={showAll}
                        onClick={() => this.setState({ showAll: !showAll })}>
                        {showAll
                            ? 'Show only files this tool accepts'
                            : `Show all files (${rest.length} more, for other tools)`}
                    </button>
                )}
                {this.props.reRunSection}
            </section>
        );
    }
}
